import React from "react";
import { useNavigate } from "react-router-dom";
import { CustomerLayout } from "@/components/Layout/CustomerLayout";
import { useCart } from "@/contexts/CartContext";
import { useLang } from "@/contexts/LanguageContext";
import { useCurrency } from "@/contexts/CurrencyContext";
import { Minus, Plus, Trash2, ShoppingCart } from "lucide-react";

export default function Cart() {
  const { cart, updateQty, subtotal, count } = useCart();
  const { t } = useLang();
  const { formatPrice } = useCurrency();
  const navigate = useNavigate();

  if (count === 0) {
    return (
      <CustomerLayout>
        <div className="max-w-2xl mx-auto px-4 py-20 text-center" data-testid="empty-cart">
          <ShoppingCart className="w-16 h-16 mx-auto text-[var(--so-text-soft)] opacity-40" />
          <h2 className="mt-6 text-2xl font-bold">{t("emptyCart")}</h2>
          <p className="text-[var(--so-text-soft)] mt-2">Explore restaurants and add tasty items to your cart.</p>
          <button onClick={() => navigate("/app")} className="so-btn-primary mt-6" data-testid="browse-btn">Browse restaurants</button>
        </div>
      </CustomerLayout>
    );
  }

  return (
    <CustomerLayout>
      <div className="max-w-2xl mx-auto px-4 py-6">
        <h1 className="text-2xl font-bold">{t("cart")}</h1>
        <div className="text-sm text-[var(--so-text-soft)] mb-4">From {cart.restaurantName}</div>
        <div className="space-y-2">
          {cart.items.map((it) => (
            <div key={it.id} className="so-card p-3 flex items-center gap-3" data-testid={`cart-item-${it.id}`}>
              <img src={it.image_url} alt={it.name} className="w-14 h-14 rounded-lg object-cover" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{it.name}</div>
                <div className="text-sm text-[var(--so-orange)] font-bold">{formatPrice(it.price * it.quantity)}</div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => updateQty(it.id, -1)} className="w-8 h-8 rounded-full border border-[var(--so-border)] flex items-center justify-center hover:border-[var(--so-orange)]" data-testid={`dec-${it.id}`}>
                  {it.quantity === 1 ? <Trash2 className="w-3.5 h-3.5" /> : <Minus className="w-3.5 h-3.5" />}
                </button>
                <span className="w-6 text-center font-bold" data-testid={`qty-${it.id}`}>{it.quantity}</span>
                <button onClick={() => updateQty(it.id, 1)} className="w-8 h-8 rounded-full bg-[var(--so-orange)] text-white flex items-center justify-center hover:bg-[var(--so-orange-hot)]" data-testid={`inc-${it.id}`}>
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 so-card p-4">
          <div className="flex justify-between text-sm">
            <span className="text-[var(--so-text-soft)]">{t("subtotal")}</span>
            <span className="font-bold" data-testid="cart-subtotal">{formatPrice(subtotal)}</span>
          </div>
        </div>

        <button onClick={() => navigate("/app/checkout")} className="so-btn-primary w-full justify-center mt-4" data-testid="proceed-checkout">
          {t("checkout")} · {formatPrice(subtotal)}
        </button>
      </div>
    </CustomerLayout>
  );
}
